import { useCallback, useMemo } from "react";
import { FiUserX } from "react-icons/fi";
import FilterButton from "components/Button/FilterButton";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import { ActiveFilter, SortOption } from "components/ControlBar/types";

export const DEFAULT_WORKFORCE_SORT = "created_at_desc";
export const DEFAULT_WORKFORCE_SORT_OPTIONS: SortOption[] = [
  { label: "Date Added: Newest First", value: "created_at_desc" },
  { label: "Date Added: Oldest First", value: "created_at_asc" },
  { label: "Name: A-Z", value: "last_name_asc" },
  { label: "Name: Z-A", value: "last_name_desc" },
];

export interface WorkforceTableFiltersType {
  q?: string;
  show_deactivated?: string;
  sort?: string;
}

interface WorkforceTableFiltersProps {
  filters: WorkforceTableFiltersType;
  onFiltersChange: (filters: WorkforceTableFiltersType) => void;
  onReset: () => void;
}

export default function WorkforceTableFilters({ filters, onFiltersChange, onReset }: WorkforceTableFiltersProps) {
  const currentSort = filters.sort || DEFAULT_WORKFORCE_SORT;

  const handleFilterChange = useCallback(
    (key: keyof WorkforceTableFiltersType, value: string | undefined) => {
      const newValue = value === "" ? undefined : value;
      if (filters[key] !== newValue) {
        onFiltersChange({
          ...filters,
          [key]: newValue,
        });
      }
    },
    [filters, onFiltersChange],
  );

  const hasActiveFilters = useMemo(
    () => Object.entries(filters).some(([key, value]) => key !== "q" && value !== undefined && value !== ""),
    [filters],
  );

  const handleToggle = useCallback(
    (key: keyof WorkforceTableFiltersType) => {
      if (filters[key] === "true") {
        handleFilterChange(key, undefined);
      } else {
        handleFilterChange(key, "true");
      }
    },
    [filters, handleFilterChange],
  );

  // Build active filters for display
  const activeFilters: ActiveFilter[] = [];

  if (filters.show_deactivated === "true") {
    activeFilters.push({
      key: "show_deactivated",
      label: "Show Deactivated Users",
      onRemove: () => handleFilterChange("show_deactivated", undefined),
    });
  }

  return (
    <FilterSortSearchBar
      searchPlaceholder="Search workforce..."
      searchValue={filters.q}
      onSearchChange={(value) => handleFilterChange("q", value)}
      currentSort={currentSort}
      defaultSort={DEFAULT_WORKFORCE_SORT}
      onSortChange={(value) => onFiltersChange({ ...filters, sort: value })}
      sortOptions={DEFAULT_WORKFORCE_SORT_OPTIONS}
      quickFilters={
        <FilterButton
          onClick={() => handleToggle("show_deactivated")}
          isActive={filters.show_deactivated === "true"}
          icon={FiUserX}
          size="sm"
        >
          Show Deactivated Users
        </FilterButton>
      }
      activeFilters={activeFilters}
      onReset={onReset}
      hasActiveFilters={hasActiveFilters}
    />
  );
}
